import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import GET_ORDER_SUPPLIERS_LIST from 'api/queries/GET_ORDER_SUPPLIERS_LIST';
import { ORDER_SUPPLIERS_PAGE_SIZE } from 'constants/pagination';
import { getStatusColor } from 'constants/statuses';
import ShowListSvg from 'images/show-list.svg';
import TimelineDoneIcon from 'images/timeline-done.svg';
import TimelineErrorIcon from 'images/timeline-error.svg';
import TimelineInProgressIcon from 'images/timeline-in-progress.svg';
import TimelineWaitingIcon from 'images/timeline-waiting.svg';
import HideListSvg from 'images/v5/icons/align-justify.svg';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import FilterSvg from 'images/v5/icons/filter-lines.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import Button from 'layout/atoms/Button/Button';
import Checkbox from 'layout/atoms/Checkbox/Checkbox';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Header from 'layout/atoms/Header/Header';
import Loader from 'layout/atoms/Loader/Loader';
import Pie from 'layout/atoms/Pie';
import TabsButton from 'layout/atoms/TabsButton/TabsButton';
import Tooltip from 'layout/atoms/Tooltip';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import Pagination from 'layout/molecules/Pagination/Pagination';
import SimpleTimeline from 'layout/molecules/SimpleTimeline';
import StickyBottom from 'layout/molecules/StickyBottom/StickyBottom';
import { selectOrderCollectionsTimelinePeriod, setOrderCollectionsTimelinePeriod, } from 'store/layoutSlice';
import { fetchOrderSuppliersPaginated, goToNextSuppliersPage, goToPreviousSuppliersPage, goToSuppliersPage, resetSuppliersPagination, selectOrderDetailsEvents, selectOrderDetailsItem, selectOrderDetailsRefetchFlags, selectOrderDetailsStatus, selectOrderDetailsSuppliers, selectOrderDetailsSuppliersCurrentPage, selectOrderDetailsSuppliersCursors, selectOrderDetailsSuppliersPageInfo, selectOrderDetailsSuppliersTotalCount, } from 'store/orderDetailsSlice';
import styles from './Collections.module.scss';
import SupplierTooltip from './SupplierTooltip';
function Collections() {
    var _a;
    const { t } = useTranslation(['orders', 'specification']);
    const dispatch = useDispatch();
    const status = useSelector(selectOrderDetailsStatus);
    const refetchFlag = useSelector(selectOrderDetailsRefetchFlags).fetchOrderVolume;
    const orderItem = useSelector(selectOrderDetailsItem);
    const orderSuppliers = useSelector(selectOrderDetailsSuppliers);
    const events = useSelector(selectOrderDetailsEvents);
    const pageInfo = useSelector(selectOrderDetailsSuppliersPageInfo);
    const currentPage = useSelector(selectOrderDetailsSuppliersCurrentPage);
    const cursors = useSelector(selectOrderDetailsSuppliersCursors);
    const totalCount = useSelector(selectOrderDetailsSuppliersTotalCount);
    const timelinePeriod = useSelector(selectOrderCollectionsTimelinePeriod);
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
    // Track previous supplierId to detect changes
    const prevSupplierIdRef = useRef(null);
    const hasInitializedRef = useRef(false);
    const pixelsPerDayMap = {
        day: 60,
        week: 30,
        month: 15,
    };
    // Initial load and refetch after delivery changes
    useEffect(() => {
        if (status.fetch === 'succeeded' &&
            status.fetchOrderVolume === 'idle' &&
            !hasInitializedRef.current) {
            // Initial load - go to page 1
            hasInitializedRef.current = true;
            dispatch(fetchOrderSuppliersPaginated({
                orderId: orderItem.id,
                first: ORDER_SUPPLIERS_PAGE_SIZE,
                supplierId: selectedSupplierId,
            }));
        }
        else if (refetchFlag) {
            // Refetch after delivery changes - preserve current page
            // cursors[0] = '' (for page 1), cursors[1] = cursor for page 2, etc.
            // To refetch page N, we need after: cursors[N-1]
            let currentCursor = null;
            if (currentPage === 1) {
                // Page 1 uses null/empty cursor
                currentCursor = null;
            }
            else if (currentPage > 1 && cursors.length >= currentPage) {
                // Page N uses cursors[N-1]
                currentCursor = cursors[currentPage - 1];
                // Convert empty string to null
                if (currentCursor === '') {
                    currentCursor = null;
                }
            }
            else {
                // Fallback: if we don't have the cursor, try to use the last known cursor
                // This shouldn't happen, but just in case
                currentCursor = cursors.length > 1 ? cursors[cursors.length - 1] : null;
                if (currentCursor === '') {
                    currentCursor = null;
                }
            }
            dispatch(fetchOrderSuppliersPaginated({
                orderId: orderItem.id,
                first: ORDER_SUPPLIERS_PAGE_SIZE,
                after: currentCursor,
                supplierId: selectedSupplierId,
            }));
        }
    }, [
        dispatch,
        orderItem.id,
        status.fetch,
        status.fetchOrderVolume,
        refetchFlag,
        currentPage,
        cursors,
        selectedSupplierId,
    ]);
    // Handle supplier filter changes separately
    useEffect(() => {
        const supplierIdChanged = prevSupplierIdRef.current !== selectedSupplierId;
        if (supplierIdChanged && hasInitializedRef.current && status.fetch === 'succeeded') {
            prevSupplierIdRef.current = selectedSupplierId;
            // Supplier filter changed - reset pagination first
            dispatch(resetSuppliersPagination());
            // Then fetch first page with new filter
            dispatch(fetchOrderSuppliersPaginated({
                orderId: orderItem.id,
                first: ORDER_SUPPLIERS_PAGE_SIZE,
                supplierId: selectedSupplierId,
            }));
        }
        else if (!hasInitializedRef.current) {
            prevSupplierIdRef.current = selectedSupplierId;
        }
    }, [dispatch, orderItem.id, selectedSupplierId, status.fetch]);
    // Calculate page numbers to display (show all available pages)
    const getPageNumbers = () => {
        // If filter is applied, count actual suppliers on current page
        let effectiveTotal = totalCount;
        if (selectedSupplierId && orderSuppliers.length > 0) {
            // Count suppliers (not variants) from the current data
            const suppliersCount = orderSuppliers.filter((item) => item.supplier === true).length;
            // If we have less suppliers than page size, we're on last page
            if (suppliersCount < ORDER_SUPPLIERS_PAGE_SIZE) {
                effectiveTotal = suppliersCount + (currentPage - 1) * ORDER_SUPPLIERS_PAGE_SIZE;
            }
            else if (!(pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage)) {
                effectiveTotal = suppliersCount + (currentPage - 1) * ORDER_SUPPLIERS_PAGE_SIZE;
            }
        }
        // If total count is null (loading), show just 1 page
        if (effectiveTotal === null) {
            return [1];
        }
        // Calculate total pages from total count and page size
        let totalPages;
        if (effectiveTotal > 0) {
            totalPages = Math.ceil(effectiveTotal / ORDER_SUPPLIERS_PAGE_SIZE);
        }
        else if ((pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage) === false) {
            totalPages = currentPage;
        }
        else {
            totalPages = Math.max(cursors.length, currentPage);
        }
        // Ensure at least 1 page is shown
        totalPages = Math.max(totalPages, 1);
        // Show all pages
        const pages = [];
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    };
    const handleNextPage = () => {
        dispatch(goToNextSuppliersPage());
    };
    const handlePreviousPage = () => {
        dispatch(goToPreviousSuppliersPage());
    };
    const handleGoToPage = (page) => {
        dispatch(goToSuppliersPage({ pageNumber: page, supplierId: selectedSupplierId }));
    };
    // Count suppliers on current page (only count supplier rows, not variants)
    const suppliersCount = orderSuppliers.filter((s) => s.supplier).length;
    const [showList, setShowList] = useState('hide');
    const [showColumns, setShowColumns] = useState({
        zam: true,
        dostawa: true,
        status: true,
    });
    // Fetch suppliers list for filter dropdown (lightweight query - just IDs and names)
    const { data: suppliersListData, loading: suppliersListLoading } = useQuery(GET_ORDER_SUPPLIERS_LIST, {
        variables: { id: orderItem.id },
        skip: !orderItem.id,
    });
    // Extract unique suppliers for filter dropdown
    const suppliersList = React.useMemo(() => {
        var _a, _b;
        if (!((_b = (_a = suppliersListData === null || suppliersListData === void 0 ? void 0 : suppliersListData.node) === null || _a === void 0 ? void 0 : _a.orderSuppliers) === null || _b === void 0 ? void 0 : _b.edges)) {
            return [];
        }
        const uniqueSuppliers = new Map();
        suppliersListData.node.orderSuppliers.edges.forEach((edge) => {
            var _a;
            const supplier = (_a = edge.node) === null || _a === void 0 ? void 0 : _a.supplier;
            if (supplier && supplier.id && !uniqueSuppliers.has(supplier.id)) {
                uniqueSuppliers.set(supplier.id, {
                    id: supplier.id,
                    name: supplier.shortName || supplier.fullName || supplier.code || '',
                });
            }
        });
        return Array.from(uniqueSuppliers.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [suppliersListData]);
    // const oneDayInMilliseconds = 7 * 24 * 60 * 60 * 1000;
    // const weekOptions = Array.from({ length: 17 }, (_, i) => {
    //   const weekStart = moment()
    //     .startOf('week')
    //     .add(i - 8, 'weeks');
    //   const weekEnd = weekStart.clone().endOf('week');
    //   const value = weekStart.format('YYYY-MM-DD');
    //   const label = `${weekStart.format('MMM DD')} - ${weekEnd.format('MMM DD, YYYY')}`;
    //   return { value, label, weekStart: weekStart.clone() };
    // });
    const goToToday = () => {
        // const start = moment().startOf('week');
        // const end = moment().endOf('week').add(7, 'days');
        // if (timelineRef.current) {
        //   timelineRef.current.updateScrollCanvas(start.valueOf(), end.valueOf());
        // }
    };
    useEffect(() => {
        goToToday();
    }, []);
    const visibleColumnsCount = Object.values(showColumns).filter(Boolean).length;
    // Calculate dynamic sidebar width: 180px (name) + (100px per visible column) + 30px (action cell)
    const labelWidth = 180 + visibleColumnsCount * 100 + 30;
    // Filter suppliers and events (no client-side supplier filtering - done on backend)
    let filteredSuppliers = showList === 'show'
        ? orderSuppliers
        : orderSuppliers.filter((item) => item.supplier === true);
    // Apply status filter if selected (filter by variant status)
    if (selectedStatusFilter) {
        // Find all variant IDs (supplier === false) that match the selected status
        const variantIdsWithStatus = orderSuppliers
            .filter((item) => item.supplier === false && item.status === selectedStatusFilter)
            .map((item) => item.id);
        // Get parent supplier IDs for these variants
        const suppliersWithFilteredVariants = new Set();
        let currentSupplierId = null;
        for (let i = 0; i < orderSuppliers.length; i += 1) {
            const item = orderSuppliers[i];
            if (item.supplier === true) {
                currentSupplierId = item.id;
            }
            else if (variantIdsWithStatus.includes(item.id) && currentSupplierId) {
                suppliersWithFilteredVariants.add(currentSupplierId);
            }
        }
        // Filter ALL items - keep suppliers with matching variants + only variants that match the status
        filteredSuppliers = orderSuppliers.filter((item) => {
            // Keep suppliers that have matching variants
            if (item.supplier === true) {
                return suppliersWithFilteredVariants.has(item.id);
            }
            // Keep variants that match the status
            return item.status === selectedStatusFilter;
        });
    }
    const filteredEvents = showList ? events.filter((item) => item.supplier === false) : events;
    const getStatusIcon = (status) => {
        switch (status) {
            case 'done':
                return React.createElement(TimelineDoneIcon, null);
            case 'in-progress':
                return React.createElement(TimelineInProgressIcon, null);
            case 'waiting':
                return React.createElement(TimelineWaitingIcon, null);
            case 'error':
                return React.createElement(TimelineErrorIcon, null);
            default:
                return 'Unknown';
        }
    };
    const renderGroupLeft = useCallback((group) => {
        return (React.createElement("div", { className: classNames(styles.groupRow, styles[`col-${visibleColumnsCount}`]) },
            React.createElement("div", { className: classNames(styles.cell, styles.name, {
                    [styles.variant]: !group.supplier,
                }), style: { width: '180px' } },
                group.supplier && (React.createElement("div", { className: styles.statusIndicator, style: { backgroundColor: getStatusColor(group.status) } })),
                React.createElement(Tooltip, { theme: "inverse", content: React.createElement(SupplierTooltip, { data: { title: group.title } }) },
                    React.createElement("p", null, group.title))),
            showColumns.zam && (React.createElement("div", { className: styles.cell, style: { width: '100px' } }, Math.round(group.zam * 1000) / 1000)),
            showColumns.dostawa && (React.createElement("div", { className: styles.cell, style: { width: '100px' } },
                React.createElement(Pie, { value: group.dostawa, total: group.zam, color: getStatusColor(group.status) }))),
            showColumns.status && (React.createElement("div", { className: styles.cell, style: { width: '100px' } }, group.statusTooltip ? (React.createElement(Tooltip, { theme: "inverse", content: group.statusTooltip, position: "top" },
                React.createElement("span", { style: {
                        margin: '0 auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '30px',
                        height: '22px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: 'white',
                        backgroundColor: getStatusColor(group.status),
                    } }, getStatusIcon(group.status)))) : (React.createElement("span", { style: {
                    margin: '0 auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '30px',
                    height: '22px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: 'white',
                    backgroundColor: getStatusColor(group.status),
                } }, getStatusIcon(group.status))))),
            React.createElement("div", { className: styles.smallCell, style: { width: '30px' } }, "-")));
    }, [showColumns, visibleColumnsCount]);
    const renderHeaderLeft = useCallback(() => {
        return (React.createElement("div", { className: classNames(styles.groupRow, styles[`col-${visibleColumnsCount}`]) },
            React.createElement("div", { className: classNames(styles.cell, styles.name, styles.header), style: { width: '180px' } }, t('specification:supplier_name')),
            showColumns.zam && (React.createElement("div", { className: classNames(styles.cell, styles.header), style: { width: '100px' } }, t('orders'))),
            showColumns.dostawa && (React.createElement("div", { className: classNames(styles.cell, styles.header), style: { width: '100px' } }, t('delivered'))),
            showColumns.status && (React.createElement("div", { className: classNames(styles.cell, styles.header), style: { width: '100px' } }, t('status'))),
            React.createElement("div", { className: classNames(styles.smallCell, styles.header), style: { width: '30px' } },
                React.createElement(Dropdown, { trigger: React.createElement(Button, { size: "xs", theme: "border", shape: "squared", rightIcon: React.createElement(PlusSvg, null) }), closeOnClick: false },
                    React.createElement(Grid, { gap: 12 },
                        React.createElement(Header, { size: "xs" }, t('SHOW_COLUMNS')),
                        React.createElement(Checkbox, { label: t('orders'), value: showColumns.zam, onClick: () => setShowColumns(Object.assign(Object.assign({}, showColumns), { zam: !showColumns.zam })) }),
                        React.createElement(Checkbox, { label: t('delivered'), value: showColumns.dostawa, onClick: () => setShowColumns(Object.assign(Object.assign({}, showColumns), { dostawa: !showColumns.dostawa })) }),
                        React.createElement(Checkbox, { label: t('status'), value: showColumns.status, onClick: () => setShowColumns(Object.assign(Object.assign({}, showColumns), { status: !showColumns.status })) }))))));
    }, [showColumns, visibleColumnsCount, t]);
    if (status.fetchOrderVolume === 'idle' || status.fetchOrderVolume === 'loading') {
        return React.createElement(Loader, null);
    }
    if (status.fetchOrderVolume === 'failed') {
        return React.createElement("p", null, "error");
    }
    // Transform events (they already have start_time, end_time, group)
    const timelineEvents = filteredEvents.map((event) => {
        // Calculate delay days if delivery is in the past and not realized
        let delayDays = null;
        let daysUntilNextEvent = null;
        if (event.end_time && event.status === 'error') {
            const now = new Date();
            const endDate = new Date(event.end_time);
            if (endDate < now) {
                delayDays = Math.ceil((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
                // Find next event chronologically
                const nextEvent = filteredEvents
                    .filter((e) => e.group === event.group) // Same supplier
                    .filter((e) => new Date(e.start_time) > endDate) // After this event
                    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())[0];
                if (nextEvent) {
                    const nextEventStart = new Date(nextEvent.start_time);
                    daysUntilNextEvent = Math.ceil((nextEventStart.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
                }
            }
        }
        return Object.assign(Object.assign({}, event), { groupId: event.group || event.id, delayDays,
            daysUntilNextEvent });
    });
    // Transform suppliers to groups
    const timelineGroups = filteredSuppliers.map((supplier) => ({
        id: supplier.id,
        title: supplier.title,
        supplier: supplier.supplier,
        zam: supplier.zam,
        dostawa: supplier.dostawa,
        status: supplier.status,
        statusTooltip: supplier.statusTooltip,
    }));
    return (React.createElement(Grid, { gap: 12, hp: 24, vp: 16 },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
            React.createElement("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '8px',
                } },
                React.createElement(TabsButton, { tabs: [
                        { id: 'hide', icon: React.createElement(HideListSvg, null) },
                        { id: 'show', icon: React.createElement(ShowListSvg, null) },
                    ], activeTab: showList, onClick: (e) => setShowList(e) }),
                React.createElement(TabsButton, { tabs: [
                        { id: 'day', label: t('common:DAY') },
                        { id: 'week', label: t('common:WEEK') },
                        { id: 'month', label: t('common:MONTH') },
                    ], activeTab: timelinePeriod, onClick: (e) => dispatch(setOrderCollectionsTimelinePeriod(e)) })),
            React.createElement("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '8px',
                } },
                React.createElement(Dropdown, { closeOnClick: false, trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(FilterSvg, null), rightIcon: React.createElement(ChevronDownSvg, null) }, selectedSupplierId
                        ? ((_a = suppliersList.find((s) => s.id === selectedSupplierId)) === null || _a === void 0 ? void 0 : _a.name) || t('suppliers')
                        : t('suppliers')) }, (close) => (React.createElement(React.Fragment, null,
                    React.createElement(Button, { align: "left", theme: selectedSupplierId === null ? 'fill' : 'ghost', onClick: () => {
                            setSelectedSupplierId(null);
                            close();
                        } }, t('common:all_v2')),
                    suppliersListLoading && (React.createElement("div", { style: { padding: '8px 16px' } },
                        React.createElement(Loader, null))),
                    !suppliersListLoading && suppliersList.length === 0 && (React.createElement(EmptyState, { noHeader: true, subheader: t('common:no_results_header') })),
                    !suppliersListLoading &&
                        suppliersList.length !== 0 &&
                        suppliersList.map((supplier) => (React.createElement(Button, { key: supplier.id, align: "left", theme: selectedSupplierId === supplier.id ? 'fill' : 'ghost', onClick: () => {
                                setSelectedSupplierId(supplier.id);
                                close();
                            } }, supplier.name)))))),
                React.createElement(Dropdown, { closeOnClick: false, trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(FilterSvg, null), rightIcon: React.createElement(ChevronDownSvg, null) }, selectedStatusFilter === null
                        ? t('status')
                        : t(`STATUS_${selectedStatusFilter.replace('-', '_').toUpperCase()}`)) }, (close) => (React.createElement(React.Fragment, null,
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === null ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter(null);
                            close();
                        } }, t('common:all_v1')),
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === 'done' ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter('done');
                            close();
                        } }, t('STATUS_DONE')),
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === 'in-progress' ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter('in-progress');
                            close();
                        } }, t('STATUS_IN_PROGRESS')),
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === 'waiting' ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter('waiting');
                            close();
                        } }, t('STATUS_WAITING')),
                    React.createElement(Button, { align: "left", theme: selectedStatusFilter === 'error' ? 'fill' : 'ghost', onClick: () => {
                            setSelectedStatusFilter('error');
                            close();
                        } }, t('STATUS_ERROR'))))))),
        timelineGroups.length === 0 ? (React.createElement(EmptyState, { noHeader: true, subheader: t('common:no_results_header') })) : (React.createElement(React.Fragment, null,
            React.createElement(SimpleTimeline, { groups: timelineGroups, events: timelineEvents, 
                /* Day view: 60px per day (widest), Week view: 30px per day, Month view: 15px per day (narrowest) */
                pixelsPerDay: pixelsPerDayMap[timelinePeriod], renderGroupLeft: renderGroupLeft, renderHeaderLeft: renderHeaderLeft, labelWidth: labelWidth, timelinePeriod: timelinePeriod }),
            pageInfo && cursors.length > 0 && (React.createElement(StickyBottom, { noMargin: true },
                React.createElement(Pagination, { currentPage: currentPage, pageNumbers: getPageNumbers(), pageSize: ORDER_SUPPLIERS_PAGE_SIZE, hasNextPage: pageInfo.hasNextPage, isLoading: status.fetchOrderVolume === 'loading', itemsCount: suppliersCount, totalCount: totalCount, onPreviousPage: handlePreviousPage, onNextPage: handleNextPage, onGoToPage: handleGoToPage })))))));
}
export default memo(withRouter(Collections));
